import type { OutputFormat, ReportCategory, ReportFirewallFamily } from '../reports/framework';

export type ReportAudience = 'BOARD' | 'REGULATOR' | 'EXECUTIVE' | 'AUDIT' | 'INVESTIGATION';

export interface SeededReportingDefinition {
  reportCode: string;
  title: string;
  description: string;
  category: ReportCategory;
  audience: ReportAudience;
  scope: 'PROVIDER_ORGANIZATION' | 'TENANT' | 'FACILITY';
  cadence: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'ANNUAL' | 'AD_HOC';
  outputFormats: OutputFormat[];
  allowedRoles: string[];
  firewallFamilies: ReportFirewallFamily[];
  sourceAuthority: string;
  metricKey: string;
}

const BOARD_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'CEO',
  'OWNER',
  'OPERATIONS_DIRECTOR',
  'CLINICAL_DIRECTOR',
  'FINANCE_DIRECTOR',
  'QUALITY_DIRECTOR',
];

const REGULATOR_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'CEO',
  'OWNER',
  'OPERATIONS_DIRECTOR',
  'CLINICAL_DIRECTOR',
  'QUALITY_DIRECTOR',
];

const EXECUTIVE_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'CEO',
  'OWNER',
  'OPERATIONS_DIRECTOR',
  'CLINICAL_DIRECTOR',
  'FINANCE_DIRECTOR',
  'QUALITY_DIRECTOR',
  'HR_DIRECTOR',
];

const AUDIT_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'CEO',
  'OWNER',
  'OPERATIONS_DIRECTOR',
  'CLINICAL_DIRECTOR',
  'QUALITY_DIRECTOR',
  'HR_DIRECTOR',
];

const FORMATS: OutputFormat[] = ['PDF', 'CSV', 'XLSX'];

const E1_SEED_DEFINITIONS: SeededReportingDefinition[] = [
  {
    reportCode: 'board_quality_of_care_quarterly',
    title: 'Board Quality of Care Quarterly',
    description:
      'Board-level quarterly quality, clinical alert, and restrictive practice indicators.',
    category: 'EXECUTIVE',
    audience: 'BOARD',
    scope: 'PROVIDER_ORGANIZATION',
    cadence: 'QUARTERLY',
    outputFormats: FORMATS,
    allowedRoles: BOARD_ROLES,
    firewallFamilies: ['SAH', 'RAC'],
    sourceAuthority: 'Aged Care Act 2024 and Aged Care Quality Standards governance evidence.',
    metricKey: 'quality_of_care',
  },
  {
    reportCode: 'board_safety_quarterly',
    title: 'Board Safety Quarterly',
    description: 'Board-level incident, complaint, and open disclosure safety indicators.',
    category: 'EXECUTIVE',
    audience: 'BOARD',
    scope: 'PROVIDER_ORGANIZATION',
    cadence: 'QUARTERLY',
    outputFormats: FORMATS,
    allowedRoles: BOARD_ROLES,
    firewallFamilies: ['SAH', 'RAC'],
    sourceAuthority: 'Aged Care Act 2024 SIRS and open disclosure governance evidence.',
    metricKey: 'safety',
  },
  {
    reportCode: 'board_workforce_quarterly',
    title: 'Board Workforce Quarterly',
    description: 'Board-level workforce capacity and compliance indicators.',
    category: 'EXECUTIVE',
    audience: 'BOARD',
    scope: 'PROVIDER_ORGANIZATION',
    cadence: 'QUARTERLY',
    outputFormats: FORMATS,
    allowedRoles: BOARD_ROLES,
    firewallFamilies: ['SHARED'],
    sourceAuthority: 'Aged Care Quality Standards workforce and governance evidence.',
    metricKey: 'workforce',
  },
  {
    reportCode: 'board_financial_quarterly',
    title: 'Board Financial Quarterly',
    description: 'Board-level invoice and claim value summary by facility.',
    category: 'EXECUTIVE',
    audience: 'BOARD',
    scope: 'PROVIDER_ORGANIZATION',
    cadence: 'QUARTERLY',
    outputFormats: FORMATS,
    allowedRoles: BOARD_ROLES,
    firewallFamilies: ['SAH', 'RAC', 'OTHER'],
    sourceAuthority: 'Provider board financial governance evidence.',
    metricKey: 'financial',
  },
  {
    reportCode: 'board_annual_statement_of_compliance',
    title: 'Board Annual Statement of Compliance',
    description: 'Annual board compliance statement preparation summary.',
    category: 'EXECUTIVE',
    audience: 'BOARD',
    scope: 'PROVIDER_ORGANIZATION',
    cadence: 'ANNUAL',
    outputFormats: FORMATS,
    allowedRoles: BOARD_ROLES,
    firewallFamilies: ['SAH', 'RAC'],
    sourceAuthority: 'Aged Care Act 2024 provider governance and compliance obligations.',
    metricKey: 'annual_compliance',
  },
  {
    reportCode: 'regulator_aihw_qi_submission_prep',
    title: 'QI quarterly summary',
    description: 'Locked residential QI ledger values; published comparison, not a target.',
    category: 'QUALITY',
    audience: 'REGULATOR',
    scope: 'PROVIDER_ORGANIZATION',
    cadence: 'QUARTERLY',
    outputFormats: FORMATS,
    allowedRoles: ['QUALITY_OFFICER', 'COMPLIANCE_OFFICER', 'FACILITY_MANAGER', 'TENANT_ADMIN'],
    firewallFamilies: ['RAC'],
    sourceAuthority: 'National Aged Care Mandatory Quality Indicator Program Manual.',
    metricKey: 'qi_submission',
  },
  {
    reportCode: 'regulator_acqsc_accreditation_evidence_pack',
    title: 'Regulator ACQSC Accreditation Evidence Pack',
    description:
      'Accreditation evidence pack summary across incidents, complaints, and quality records.',
    category: 'QUALITY',
    audience: 'REGULATOR',
    scope: 'PROVIDER_ORGANIZATION',
    cadence: 'AD_HOC',
    outputFormats: FORMATS,
    allowedRoles: REGULATOR_ROLES,
    firewallFamilies: ['SAH', 'RAC'],
    sourceAuthority: 'Aged Care Quality and Safety Commission accreditation evidence expectations.',
    metricKey: 'accreditation_evidence',
  },
  {
    reportCode: 'regulator_doh_workforce_submission',
    title: 'Regulator Department Workforce Submission',
    description: 'Workforce submission preparation summary for Department reporting.',
    category: 'PEOPLE',
    audience: 'REGULATOR',
    scope: 'PROVIDER_ORGANIZATION',
    cadence: 'MONTHLY',
    outputFormats: FORMATS,
    allowedRoles: [...REGULATOR_ROLES, 'HR_DIRECTOR'],
    firewallFamilies: ['SHARED'],
    sourceAuthority: 'Department aged care workforce reporting guidance.',
    metricKey: 'workforce_submission',
  },
  {
    reportCode: 'regulator_ndis_provider_self_report',
    title: 'Regulator NDIS Provider Self Report',
    description: 'NDIS provider self-report preparation summary for mixed providers.',
    category: 'QUALITY',
    audience: 'REGULATOR',
    scope: 'PROVIDER_ORGANIZATION',
    cadence: 'AD_HOC',
    outputFormats: FORMATS,
    allowedRoles: REGULATOR_ROLES,
    firewallFamilies: ['OTHER'],
    sourceAuthority: 'NDIS provider incident and quality self-reporting obligations.',
    metricKey: 'ndis_self_report',
  },
  {
    reportCode: 'executive_daily_occupancy',
    title: 'Executive Daily Occupancy',
    description: 'Daily executive occupancy and active client count by facility.',
    category: 'EXECUTIVE',
    audience: 'EXECUTIVE',
    scope: 'PROVIDER_ORGANIZATION',
    cadence: 'DAILY',
    outputFormats: FORMATS,
    allowedRoles: EXECUTIVE_ROLES,
    firewallFamilies: ['RAC'],
    sourceAuthority: 'Provider executive operating cadence.',
    metricKey: 'occupancy',
  },
  {
    reportCode: 'executive_daily_care_minutes',
    title: 'Executive Daily Care Minutes',
    description: 'Daily care minutes summary using canonical timesheet and client facility links.',
    category: 'EXECUTIVE',
    audience: 'EXECUTIVE',
    scope: 'PROVIDER_ORGANIZATION',
    cadence: 'DAILY',
    outputFormats: FORMATS,
    allowedRoles: EXECUTIVE_ROLES,
    firewallFamilies: ['RAC'],
    sourceAuthority: 'AN-ACC care minutes governance and provider operating cadence.',
    metricKey: 'care_minutes',
  },
  {
    reportCode: 'executive_weekly_workforce',
    title: 'Executive Weekly Workforce',
    description: 'Weekly executive workforce coverage summary by facility.',
    category: 'EXECUTIVE',
    audience: 'EXECUTIVE',
    scope: 'PROVIDER_ORGANIZATION',
    cadence: 'WEEKLY',
    outputFormats: FORMATS,
    allowedRoles: EXECUTIVE_ROLES,
    firewallFamilies: ['SHARED'],
    sourceAuthority: 'Provider workforce governance evidence.',
    metricKey: 'weekly_workforce',
  },
  {
    reportCode: 'executive_monthly_clinical_governance',
    title: 'Executive Monthly Clinical Governance',
    description:
      'Monthly clinical governance summary across alerts, incidents, and restrictive practices.',
    category: 'EXECUTIVE',
    audience: 'EXECUTIVE',
    scope: 'PROVIDER_ORGANIZATION',
    cadence: 'MONTHLY',
    outputFormats: FORMATS,
    allowedRoles: EXECUTIVE_ROLES,
    firewallFamilies: ['SAH', 'RAC'],
    sourceAuthority: 'Aged Care Quality Standards clinical governance evidence.',
    metricKey: 'clinical_governance',
  },
  {
    reportCode: 'executive_monthly_financial_summary',
    title: 'Executive Monthly Financial Summary',
    description: 'Monthly invoice and claim value summary by facility.',
    category: 'EXECUTIVE',
    audience: 'EXECUTIVE',
    scope: 'PROVIDER_ORGANIZATION',
    cadence: 'MONTHLY',
    outputFormats: FORMATS,
    allowedRoles: EXECUTIVE_ROLES,
    firewallFamilies: ['SAH', 'RAC', 'OTHER'],
    sourceAuthority: 'Provider executive financial governance evidence.',
    metricKey: 'monthly_finance',
  },
  {
    reportCode: 'audit_incident_evidence_chain',
    title: 'Audit Incident Evidence Chain',
    description: 'Audit report of incident lifecycle evidence and unresolved incident counts.',
    category: 'QUALITY',
    audience: 'AUDIT',
    scope: 'FACILITY',
    cadence: 'AD_HOC',
    outputFormats: FORMATS,
    allowedRoles: AUDIT_ROLES,
    firewallFamilies: ['SAH', 'RAC'],
    sourceAuthority: 'Aged Care Act 2024 SIRS and incident evidence obligations.',
    metricKey: 'incident_evidence',
  },
  {
    reportCode: 'audit_restrictive_practice_register',
    title: 'Audit Restrictive Practice Register',
    description: 'Restrictive practice register audit summary by facility and status.',
    category: 'QUALITY',
    audience: 'AUDIT',
    scope: 'FACILITY',
    cadence: 'MONTHLY',
    outputFormats: FORMATS,
    allowedRoles: AUDIT_ROLES,
    firewallFamilies: ['SAH', 'RAC'],
    sourceAuthority: 'Aged Care restrictive practices governance obligations.',
    metricKey: 'restrictive_practice_register',
  },
  {
    reportCode: 'audit_open_disclosure_tracker',
    title: 'Audit Open Disclosure Tracker',
    description: 'Open disclosure tracker summary for incidents and complaints.',
    category: 'QUALITY',
    audience: 'AUDIT',
    scope: 'FACILITY',
    cadence: 'MONTHLY',
    outputFormats: FORMATS,
    allowedRoles: AUDIT_ROLES,
    firewallFamilies: ['SAH', 'RAC'],
    sourceAuthority: 'Open disclosure expectations in aged care quality governance.',
    metricKey: 'open_disclosure',
  },
  {
    reportCode: 'audit_worker_compliance_pack',
    title: 'Audit Worker Compliance Pack',
    description: 'Worker screening and compliance pack summary for audit review.',
    category: 'PEOPLE',
    audience: 'AUDIT',
    scope: 'FACILITY',
    cadence: 'MONTHLY',
    outputFormats: FORMATS,
    allowedRoles: [...AUDIT_ROLES, 'HR_DIRECTOR'],
    firewallFamilies: ['SHARED'],
    sourceAuthority: 'Aged care workforce screening and provider compliance evidence.',
    metricKey: 'worker_compliance',
  },
  {
    reportCode: 'investigation_complaints_detail',
    title: 'Investigation Complaints Detail',
    description: 'Investigation-ready complaints detail summary by facility and status.',
    category: 'QUALITY',
    audience: 'INVESTIGATION',
    scope: 'FACILITY',
    cadence: 'AD_HOC',
    outputFormats: FORMATS,
    allowedRoles: AUDIT_ROLES,
    firewallFamilies: ['SAH', 'RAC'],
    sourceAuthority: 'Aged care complaints and investigation evidence expectations.',
    metricKey: 'complaints_detail',
  },
  {
    reportCode: 'investigation_clinical_alerts_detail',
    title: 'Investigation Clinical Alerts Detail',
    description: 'Investigation-ready clinical alert detail summary by facility and status.',
    category: 'CLINICAL',
    audience: 'INVESTIGATION',
    scope: 'FACILITY',
    cadence: 'AD_HOC',
    outputFormats: FORMATS,
    allowedRoles: AUDIT_ROLES,
    firewallFamilies: ['SAH', 'RAC'],
    sourceAuthority: 'Clinical governance investigation evidence expectations.',
    metricKey: 'clinical_alerts_detail',
  },
];

/**
 * Every E1 report runs the same generic per-facility count query (open
 * incidents, complaints, alerts, restrictive practices, active clients,
 * linked staff, invoice and claim totals). None of them produces a
 * submission-ready board or regulator return, so the catalogue says so
 * rather than letting a title like "Department Workforce Submission" imply
 * otherwise. Replace an entry with a purpose-built report to drop the label.
 */
export const E1_SUMMARY_COUNTS_NOTICE =
  'Summary counts per facility only; not a submission-ready return.';

export const E1_REPORT_DEFINITIONS: SeededReportingDefinition[] = E1_SEED_DEFINITIONS.map(
  (seed) => ({
    ...seed,
    title: `${seed.title} (summary counts)`,
    description: `${seed.description} ${E1_SUMMARY_COUNTS_NOTICE}`,
  }),
);

export const E1_BOARD_REPORT_CODES = E1_REPORT_DEFINITIONS.filter(
  (definition) => definition.audience === 'BOARD',
).map((definition) => definition.reportCode);
